/**
 * @busmgr/blocks - Shared styled-components primitives
 * All blocks import from here to ensure visual consistency.
 * CSS custom properties (var(--color-*)) are set by SiteContext on :root.
 */
import styled, { css, keyframes } from 'styled-components';
import { FULL_BLEED_CSS } from '../utils/blockPresentation';

// ─── Tokens (fallback values when CSS vars not set) ─────────────────────────
export const t = {
  primary:     'var(--color-primary, #4a6741)',
  secondary:   'var(--color-secondary, #8b7355)',
  accent:      'var(--color-accent, #d4a574)',
  bg:          'var(--color-background, #fdfbf7)',
  bgAlt:       'var(--color-background-alt, #f5f1eb)',
  text:        'var(--color-text, #333333)',
  textLight:   'var(--color-text-light, #666666)',
  border:      'var(--color-border, #e0d5c7)',
  fontHeading: 'var(--font-heading, "Playfair Display", serif)',
  fontBody:    'var(--font-body, "Open Sans", sans-serif)',
};

// ─── Padding mixin ───────────────────────────────────────────────────────────
export const paddingMixin = ({ $padding = 'medium' }) => css`
  ${$padding === 'none'   && 'padding-top: 0; padding-bottom: 0;'}
  ${$padding === 'small'  && 'padding-top: 24px; padding-bottom: 24px;'}
  ${$padding === 'medium' && 'padding-top: 48px; padding-bottom: 48px;'}
  ${$padding === 'large'  && 'padding-top: 80px; padding-bottom: 80px;'}
  ${$padding === 'xlarge' && 'padding-top: 120px; padding-bottom: 120px;'}
  @media (max-width: 768px) {
    ${($padding === 'large' || $padding === 'xlarge') && 'padding-top: 48px; padding-bottom: 48px;'}
  }
`;

// ─── Full-bleed breakout (only when $fullBleed — absent = no-op) ─────────────
export const fullBleedMixin = ({ $fullBleed }) => ($fullBleed ? css`${FULL_BLEED_CSS}` : '');

// ─── Block section ───────────────────────────────────────────────────────────
export const Section = styled.section`
  position: relative;
  width: 100%;
  ${paddingMixin}
  ${fullBleedMixin}
  background-color: ${({ $bgColor }) => $bgColor || 'transparent'};
  color: ${({ $textColor }) => $textColor || 'inherit'};
`;

// ─── Container ───────────────────────────────────────────────────────────────
// $fullBleed removes the 1200px cap so media can run edge-to-edge inside a
// full-bleed section. Default (no prop) keeps today's centered column.
export const Container = styled.div`
  max-width: ${({ $fullBleed }) => ($fullBleed ? 'none' : '1200px')};
  margin: 0 auto;
  padding: ${({ $fullBleed }) => ($fullBleed ? '0' : '0 24px')};
  @media (max-width: 768px) {
    padding: ${({ $fullBleed }) => ($fullBleed ? '0' : '0 16px')};
  }
`;

// ─── Typography ──────────────────────────────────────────────────────────────
export const BlockTitle = styled.h2`
  margin: 0 0 16px 0;
  font-family: ${t.fontHeading};
  font-size: 2.25rem;
  font-weight: 600;
  color: ${({ $color }) => $color || t.text};
  @media (max-width: 1024px) { font-size: 2rem; }
  @media (max-width: 768px)  { font-size: 1.75rem; }
`;

export const BlockSubtitle = styled.p`
  margin: 0;
  font-size: 1.125rem;
  color: ${t.textLight};
  max-width: 600px;
`;

export const BlockHeader = styled.div`
  margin-bottom: 40px;
  ${({ $center }) => $center && css`
    text-align: center;
    ${BlockSubtitle} { margin-left: auto; margin-right: auto; }
  `}
`;

export const RichText = styled.div`
  font-family: ${t.fontBody};
  font-size: 1rem;
  line-height: 1.7;
  color: ${t.text};
  h1, h2, h3, h4 {
    font-family: ${t.fontHeading};
    margin-top: 1.5em;
    margin-bottom: 0.5em;
  }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.5rem; }
  h4 { font-size: 1.25rem; }
  p  { margin: 0 0 1em 0; }
  strong { font-weight: 600; }
  blockquote {
    margin: 1.5em 0; padding: 1em 1.5em;
    border-left: 4px solid ${t.primary};
    background: ${t.bgAlt};
    font-style: italic;
  }
  a { color: ${t.primary}; text-decoration: underline; }
  ul, ol { margin: 1em 0; padding-left: 1.5em; }
  li { margin-bottom: 0.5em; }
`;

// ─── Buttons ─────────────────────────────────────────────────────────────────
// Note: Use the `as` prop to render as <a> or react-router <Link>
// e.g. <BtnPrimary as={Link} to="/shop"> or <BtnPrimary as="a" href="...">

const btnBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px solid transparent;
  border-radius: 4px;
  font-family: ${t.fontBody};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: ${({ $size }) => $size === 'lg' ? '16px 32px' : $size === 'sm' ? '8px 16px' : '12px 24px'};
  font-size: ${({ $size }) => $size === 'lg' ? '1.125rem' : $size === 'sm' ? '0.875rem' : '1rem'};
`;

export const BtnPrimary = styled.button`
  ${btnBase}
  background: ${t.primary}; color: #fff; border-color: ${t.primary};
  &:hover { background: ${t.secondary}; border-color: ${t.secondary}; }
`;

export const BtnOutline = styled.button`
  ${btnBase}
  background: transparent; color: ${t.primary}; border-color: ${t.primary};
  &:hover { background: ${t.primary}; color: #fff; }
`;

export const BtnWhite = styled.button`
  ${btnBase}
  background: #fff; color: ${t.primary}; border-color: #fff;
  &:hover { background: rgba(255,255,255,0.9); }
`;

export const BtnOutlineWhite = styled.button`
  ${btnBase}
  background: transparent; color: #fff; border-color: #fff;
  &:hover { background: #fff; color: ${t.primary}; }
`;

// ─── Spin animation (for loaders) ────────────────────────────────────────────
export const spin = keyframes`to { transform: rotate(360deg); }`;
